// src/components/CardEditor/CardEditor.tsx

import React, { useState, useEffect } from 'react';
import { Copy, BookOpen, Trash2, ArrowUp, ClipboardCopy, Check, AlertTriangle } from 'lucide-react';
import { CardSide } from './CardSide';
import DragHandle from '../../components/deck_subdeck/DragHandle';
import './CardEditor.css';
import { ReferencesModal } from '../CardEditor/ReferencesModal';
import { SupportEditor } from '../CardEditor/SupportEditor';
import { CardErrorModal, CardErrorReport } from './CardErrorModal';

interface CardEditorProps {
    cardNumber: number;
    front: string;
    back: string;
    references?: string;
    supportContent?: string;
    onFrontChange: (content: string) => void;
    onBackChange: (content: string) => void;
    onDelete: () => void;
    onCopy?: () => void;
    onReferencesChange?: (content: string) => void;
    onSupportContentChange?: (content: string) => void;
    dragHandleProps?: any;
    isDragging?: boolean;
    subdeckName?: string;
    expandAllSupport?: boolean;
    isRestricted?: boolean;
    /** Erros reportados (não negados) deste card específico — vazio/undefined = sem erro, some o ícone. */
    errorReports?: CardErrorReport[];
    isAdmin?: boolean;
    onUpdateErrorStatus?: (errorId: string, status: 'accepted' | 'denied') => Promise<void> | void;
    onDeleteErrorReport?: (errorId: string) => Promise<void> | void;
    /** Card alvo de um deep-link (?cardId=... vindo do painel de erros) — realça visualmente. */
    isHighlighted?: boolean;
}

const stripHtml = (html: string): string => {
    if (!html) return '';
    return html
        .replace(/<img[^>]*>/gi, '[imagem]')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

export const CardEditor: React.FC<CardEditorProps> = ({
    cardNumber,
    front,
    back,
    references = '',
    supportContent = '',
    onFrontChange,
    onBackChange,
    onReferencesChange,
    onSupportContentChange,
    onDelete,
    onCopy,
    dragHandleProps,
    isDragging = false,
    subdeckName = 'geral',
    expandAllSupport,
    isRestricted = false,
    errorReports,
    isAdmin = false,
    onUpdateErrorStatus,
    onDeleteErrorReport,
    isHighlighted = false
}) => {
    const [showSupportContent, setShowSupportContent] = useState(false);
    const [showReferencesModal, setShowReferencesModal] = useState(false);
    const [copyTextDone, setCopyTextDone] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const hasError = !!errorReports && errorReports.length > 0;

    // Sincroniza estado local com o toggle global do EditorPage
    useEffect(() => {
        if (expandAllSupport !== undefined) {
            setShowSupportContent(expandAllSupport);
        }
    }, [expandAllSupport]);

    const handleSaveReferences = (content: string) => {
        if (onReferencesChange) {
            onReferencesChange(content);
        }
    };

    const handleCopyText = () => {
        const lines: string[] = [
            `Pergunta:\n${stripHtml(front)}`,
            `\nResposta:\n${stripHtml(back)}`
        ];
        const support = stripHtml(supportContent);
        if (support) lines.push(`\nConteúdo de Apoio:\n${support}`);

        navigator.clipboard.writeText(lines.join('\n')).then(() => {
            setCopyTextDone(true);
            setTimeout(() => setCopyTextDone(false), 2000);
        });
    };

    return (
        <>
            <div
                className={`cred-card${isHighlighted ? ' cred-card--highlighted' : ''}`}
                style={{
                    opacity: isDragging ? 0.5 : 1,
                    transform: isDragging ? 'rotate(2deg)' : 'none'
                }}
            >
                {/* ===== TOPO DO CARD ===== */}
                <div className="cred-top-bar">
                    <div className="cred-top-left">
                        <div {...dragHandleProps}>
                            <DragHandle />
                        </div>
                        <span className="cred-card-number">Card {cardNumber}</span>
                    </div>

                    <div className="cred-top-right">
                        <button
                            type="button"
                            className="cred-btn cred-btn-copy"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            title="Ir ao topo"
                        >
                            <ArrowUp size={16} />
                        </button>
                        {!isRestricted && onCopy && (
                            <button
                                type="button"
                                className="cred-btn cred-btn-copy"
                                onClick={onCopy}
                                title="Copiar card"
                            >
                                <Copy size={16} />
                                <span>Copiar</span>
                            </button>
                        )}
                        {!isRestricted && (
                            <button
                                type="button"
                                className={`cred-btn cred-btn-copy-text${copyTextDone ? ' cred-btn-copy-text--done' : ''}`}
                                onClick={handleCopyText}
                                title={copyTextDone ? 'Copiado!' : 'Copiar texto do card'}
                            >
                                {copyTextDone ? <Check size={16} /> : <ClipboardCopy size={16} />}
                                <span>{copyTextDone ? 'Copiado!' : 'Copiar texto'}</span>
                            </button>
                        )}
                        {!isRestricted && (
                            <button
                                type="button"
                                className={`cred-btn cred-btn-references ${!references || references.trim() === '' ? 'cred-btn-references--empty' : ''}`}
                                onClick={() => setShowReferencesModal(true)}
                                title="Gerenciar referências"
                            >
                                <BookOpen size={16} />
                                <span>Referências</span>
                            </button>
                        )}
                        {hasError && (
                            <button
                                type="button"
                                className="cred-btn cred-btn-error"
                                onClick={() => setShowErrorModal(true)}
                                title={`${errorReports!.length} erro${errorReports!.length > 1 ? 's' : ''} reportado${errorReports!.length > 1 ? 's' : ''} neste card`}
                            >
                                <AlertTriangle size={16} />
                                <span>Erro reportado</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className="cred-btn cred-btn-delete"
                            onClick={onDelete}
                            title="Deletar card"
                        >
                            <Trash2 size={16} />
                            <span>Deletar</span>
                        </button>
                    </div>
                </div>

                {/* ===== PERGUNTA E RESPOSTA (2 COLUNAS) ===== */}
                <div className="cred-columns-container">
                    <CardSide
                        label="Pergunta"
                        content={front}
                        setContent={onFrontChange}
                        editorType="front"
                        editorId={`card-${cardNumber}-front`}
                        subdeckName={subdeckName}
                        isRestricted={isRestricted}
                    />

                    <div className="cred-column-divider" />

                    <CardSide
                        label="Resposta"
                        content={back}
                        setContent={onBackChange}
                        editorType="back"
                        editorId={`card-${cardNumber}-back`}
                        subdeckName={subdeckName}
                        isRestricted={isRestricted}
                    />
                </div>

                {/* ===== CONTEÚDO DE APOIO (RECOLHÍVEL) ===== */}
                <div className="cred-support-toggle">
                    <button
                        type="button"
                        className="cred-btn-toggle-support"
                        onClick={() => setShowSupportContent(!showSupportContent)}
                    >
                        <span className="cred-toggle-icon">{showSupportContent ? '▼' : '▶'}</span>
                        Conteúdo de Apoio
                    </button>
                </div>

                {showSupportContent && (
                    <div className="cred-support-area">
                        <label className="cred-support-label">Conteúdo de Apoio</label>
                        <p className="cred-support-help">
                            Use este espaço para adicionar explicações, contexto ou materiais complementares
                        </p>
                        <SupportEditor
                            content={supportContent}
                            setContent={(content) => onSupportContentChange?.(content)}
                            placeholder="Digite o conteúdo de apoio..."
                            subdeckName={subdeckName}
                        />
                    </div>
                )}
            </div>

            {!isRestricted && (
                <ReferencesModal
                    isOpen={showReferencesModal}
                    initialContent={references}
                    onSave={handleSaveReferences}
                    onClose={() => setShowReferencesModal(false)}
                />
            )}

            {showErrorModal && hasError && (
                <CardErrorModal
                    reports={errorReports!}
                    isAdmin={isAdmin}
                    onClose={() => setShowErrorModal(false)}
                    onUpdateStatus={async (errorId, status) => { await onUpdateErrorStatus?.(errorId, status); }}
                    onDelete={async (errorId) => { await onDeleteErrorReport?.(errorId); setShowErrorModal(false); }}
                />
            )}
        </>
    );
};
